import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import aboutData from "../../data/pages/about.json";
import type { AboutPageTemplate } from "../../types/aboutPageTemplate";
import { resolveAssetUrl } from "../../utils/resolveAssetUrl";

interface PersonInfo {
  image: string;
  name: string;
  title: string;
  about?: string;
}

const typedAboutData = aboutData as AboutPageTemplate;

const founders: PersonInfo[] = typedAboutData.founders.map((person) => ({
  image: resolveAssetUrl(person.imageSrc),
  name: person.name,
  title: person.title,
  about: person.about,
}));

const heroes: PersonInfo[] = typedAboutData.heroes.map((person) => ({
  image: resolveAssetUrl(person.imageSrc),
  name: person.name,
  title: person.title,
  about: person.about,
}));

function Person({ image, name, title, about }: PersonInfo) {
  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        minWidth: 0,
        maxWidth: "336px",
        flexDirection: "column",
        alignItems: "center",
        gap: "32px",
        borderRadius: "8px",
      }}
      key={name}
    >
      <Box
        component="img"
        src={image}
        alt={name}
        sx={{
          height: "250px",
          width: "250px",
          borderRadius: "300px",
          objectFit: "cover",
        }}
      />
      <Stack sx={{ gap: 1 }}>
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{ color: "#FFF", textAlign: "center" }}
        >
          {name}
        </Typography>
        <Typography variant="body1" sx={{ color: "#E0", textAlign: "center" }}>
          {title}
        </Typography>
        {about && <Typography variant="body1">{about}</Typography>}
      </Stack>
    </Box>
  );
}

interface PeopleProps {
  foundersTitle: string;
  heroesTitle: string;
}

export function People({ foundersTitle, heroesTitle }: PeopleProps) {
  return (
    <>
      <Stack sx={{ width: "100%", gap: 4, alignItems: "center" }}>
        <Typography
          sx={{
            color: "#FFF",
            fontSize: "1.5rem",
            lineHeight: "145%",
          }}
        >
          {foundersTitle}
        </Typography>
        <Box
          sx={{
            width: "100%",
            maxWidth: "1072px",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 4,
            "& > *": {
              flex: "1 1 287px",
            },
          }}
        >
          {founders.map(({ image, name, title, about }) => (
            <Person
              key={name}
              name={name}
              image={image}
              title={title}
              about={about}
            />
          ))}
        </Box>
      </Stack>
      <Stack sx={{ width: "100%", gap: 4, alignItems: "center" }}>
        <Typography
          sx={{
            color: "#FFF",
            fontSize: "1.5rem",
            lineHeight: "145%",
          }}
        >
          {heroesTitle}
        </Typography>
        <Box
          sx={{
            width: "100%",
            maxWidth: "1072px",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 4,
            "& > *": {
              flex: "1 1 287px",
            },
          }}
        >
          {heroes.map(({ image, name, title }) => (
            <Person key={name} name={name} image={image} title={title} />
          ))}
        </Box>
      </Stack>
    </>
  );
}
